import type { AuditEntry } from "@/gql/graphql";
import { timezoneUtils } from "@/lib/timezone";
import { PrismaFieldFormatter } from "../formatters/prisma-field-formatter";
import type { AuditOperation, AuditTableFilter } from "../types";
import { AuditFieldChange, FormattedFieldChange } from "../types/audit-types";

/**
 * Modern audit utilities using Prisma metadata
 */

/**
 * Generate a custom title for audit entries based on table and changes
 */
export function generateAuditEntryTitle(entry: AuditEntry): string {
  const { tableName, operation, changes, metadata } = entry;

  // For appointments, try to generate a custom title (case-insensitive)
  if (tableName.toLowerCase() === "appointment") {
    let guestName = "Unknown Guest";
    let appointmentDate = "";

    // First check if we have metadata with customer name
    if (metadata && (metadata as any).customerName) {
      guestName = (metadata as any).customerName;
    } else {
      // Fall back to parsing field changes if no metadata
      // Look for customer information in changes
      for (const change of changes) {
        // Check for direct customer name fields
        if (change.field === "customerFirstName" && change.newValue) {
          guestName = change.newValue;
        } else if (
          change.field === "customerLastName" &&
          change.newValue &&
          guestName !== "Unknown Guest"
        ) {
          guestName = `${guestName} ${change.newValue}`;
        }

        // Check for customer relation changes
        if (change.field === "customer" && change.newValue) {
          try {
            const customerData = JSON.parse(change.newValue);
            if (customerData.firstName || customerData.lastName) {
              guestName =
                `${customerData.firstName || ""} ${customerData.lastName || ""}`.trim() ||
                "Unknown Guest";
            }
          } catch (_e) {
            // If parsing fails, use the string value
            if (change.newValue.includes(" ")) {
              guestName = change.newValue;
            }
          }
        }

        // Check if it's a related model field for Customer
        if (change.relatedModel === "Customer" && change.newValue) {
          // This might be a customer name or reference
          guestName = change.newValue;
        }

        // Check for date fields
        if ((change.field === "startTime" || change.field === "endTime") && change.newValue) {
          try {
            const dateTime = timezoneUtils.fromISO(change.newValue);
            appointmentDate = dateTime.toFormat("MMM d, yyyy");
            break; // Use the first date we find
          } catch (_e) {
            // Fallback to simple date parsing
            try {
              appointmentDate = new Date(change.newValue).toLocaleDateString();
            } catch (_dateError) {
              // Skip if date parsing fails
            }
          }
        }
      }

      // If we still don't have guest name, check old values
      if (guestName === "Unknown Guest") {
        for (const change of changes) {
          if (change.field === "customerFirstName" && change.oldValue) {
            guestName = change.oldValue;
          } else if (
            change.field === "customerLastName" &&
            change.oldValue &&
            guestName !== "Unknown Guest"
          ) {
            guestName = `${guestName} ${change.oldValue}`;
          }

          if (change.field === "customer" && change.oldValue) {
            try {
              const customerData = JSON.parse(change.oldValue);
              if (customerData.firstName || customerData.lastName) {
                guestName =
                  `${customerData.firstName || ""} ${customerData.lastName || ""}`.trim() ||
                  "Unknown Guest";
              }
            } catch (_e) {
              if (change.oldValue.includes(" ")) {
                guestName = change.oldValue;
              }
            }
          }

          // Check if it's a related model field for Customer
          if (change.relatedModel === "Customer" && change.oldValue) {
            guestName = change.oldValue;
          }
        }
      }
    }

    // If we still don't have a date, check old values
    if (!appointmentDate) {
      for (const change of changes) {
        if ((change.field === "startTime" || change.field === "endTime") && change.oldValue) {
          try {
            const dateTime = timezoneUtils.fromISO(change.oldValue);
            appointmentDate = dateTime.toFormat("MMM d, yyyy");
            break;
          } catch (_e) {
            try {
              appointmentDate = new Date(change.oldValue).toLocaleDateString();
            } catch (_dateError) {
              // Skip if date parsing fails
            }
          }
        }
      }
    }

    // Build the title
    if (appointmentDate) {
      return `Appointment for ${guestName} at ${appointmentDate}`;
    }
    return `Appointment for ${guestName}`;
  }

  // For other entities, use the table display name with operation
  const displayName = getTableDisplayName(tableName);
  const operationName = getOperationDisplayName(operation as AuditOperation);

  return `${displayName} ${operationName}`;
}

/**
 * Format audit field changes for display using Prisma metadata
 */
export function formatAuditChanges(changes: AuditFieldChange[]): {
  mainChanges: FormattedFieldChange[];
  idFields: FormattedFieldChange[];
} {
  const mainChanges: FormattedFieldChange[] = [];
  const allIdFields: FormattedFieldChange[] = [];

  for (const change of changes) {
    // Skip excluded fields
    if (PrismaFieldFormatter.shouldExcludeField(change.field)) {
      // If it's an ID field, add to ID fields
      if (change.field.endsWith("Id")) {
        const formatted = PrismaFieldFormatter.formatFieldChange(change);
        if (formatted) {
          allIdFields.push(formatted);
        }
      }
      continue;
    }

    const formatted = PrismaFieldFormatter.formatFieldChange(change);
    if (formatted) {
      mainChanges.push(formatted);
    }
  }

  return {
    mainChanges,
    idFields: allIdFields,
  };
}

/**
 * Get a summary description of the changes based on Prisma types
 */
export function getChangesSummary(changes: AuditFieldChange[]): string {
  if (changes.length === 0) return "No changes";

  // Filter out ID fields for summary
  const mainChanges = changes.filter((c) => !PrismaFieldFormatter.shouldExcludeField(c.field));

  if (mainChanges.length === 0) return "Updated IDs";

  if (mainChanges.length === 1) {
    const change = mainChanges[0];
    const displayName = getFieldDisplayName(change);
    return `Updated ${displayName}`;
  }

  // Group by Prisma types for better summaries
  const relationChanges = mainChanges.filter((c) => c.isRelation);
  const enumChanges = mainChanges.filter((c) => c.fieldKind === "enum");
  const priceChanges = mainChanges.filter(
    (c) => c.field.toLowerCase().includes("price") || c.field.toLowerCase().includes("cost")
  );

  if (relationChanges.length > 0) {
    const relations = [...new Set(relationChanges.map((c) => c.relatedModel))];
    if (relations.length === 1) {
      return `Updated ${relations[0]}${relationChanges.length > 1 ? "s" : ""}`;
    }
  }

  if (priceChanges.length > 0) {
    return `Updated ${priceChanges.length} price${priceChanges.length > 1 ? "s" : ""}`;
  }

  if (enumChanges.length > 0) {
    return `Updated ${enumChanges.length} status${enumChanges.length > 1 ? "es" : ""}`;
  }

  return `Updated ${mainChanges.length} field${mainChanges.length > 1 ? "s" : ""}`;
}

/**
 * Get display name for a field using Prisma metadata
 */
function getFieldDisplayName(change: AuditFieldChange): string {
  if (change.relatedModel) {
    return change.relatedModel;
  }

  if (
    change.prismaType &&
    (change.prismaType.includes("Status") || change.prismaType.includes("Outcome"))
  ) {
    return change.prismaType
      .replace(/([A-Z])/g, " $1")
      .replace(/^Appointment /, "")
      .trim();
  }

  return change.field
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Group changes by their Prisma type for organized display
 */
export function groupChangesByPrismaType(
  changes: AuditFieldChange[]
): Record<string, AuditFieldChange[]> {
  return changes.reduce(
    (groups, change) => {
      let groupKey = "Other";

      if (change.isRelation && change.relatedModel) {
        groupKey = `Relations (${change.relatedModel})`;
      } else if (change.fieldKind === "enum") {
        groupKey = "Enums";
      } else if (change.fieldKind === "scalar") {
        if (change.prismaType === "DateTime") {
          groupKey = "Dates";
        } else if (["Int", "Float", "Decimal"].includes(change.prismaType || "")) {
          groupKey = "Numbers";
        } else if (change.prismaType === "Boolean") {
          groupKey = "Booleans";
        } else {
          groupKey = "Text";
        }
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(change);
      return groups;
    },
    {} as Record<string, AuditFieldChange[]>
  );
}

/**
 * Check if any changes are considered "important" based on Prisma metadata
 */
export function hasImportantChanges(changes: AuditFieldChange[]): boolean {
  return changes.some(
    (change) =>
      change.isRelation ||
      change.fieldKind === "enum" ||
      change.field.toLowerCase().includes("price") ||
      change.field.toLowerCase().includes("status")
  );
}

/**
 * Get the most significant change for summary display using Prisma metadata
 */
export function getMostSignificantChange(changes: AuditFieldChange[]): AuditFieldChange | null {
  if (changes.length === 0) return null;

  // Filter out ID fields
  const mainChanges = changes.filter((c) => !PrismaFieldFormatter.shouldExcludeField(c.field));
  if (mainChanges.length === 0) return changes[0];

  // Priority order based on Prisma metadata
  const priorities = [
    // Relations are most important
    (c: AuditFieldChange) => c.isRelation,
    // Price fields
    (c: AuditFieldChange) =>
      c.field.toLowerCase().includes("price") || c.field.toLowerCase().includes("cost"),
    // Enum fields (status, etc.)
    (c: AuditFieldChange) => c.fieldKind === "enum",
    // DateTime fields
    (c: AuditFieldChange) => c.prismaType === "DateTime",
    // Boolean fields
    (c: AuditFieldChange) => c.prismaType === "Boolean",
    // Everything else
    () => true,
  ];

  for (const priorityFn of priorities) {
    const change = mainChanges.find(priorityFn);
    if (change) return change;
  }

  return mainChanges[0];
}

// Legacy utilities kept for backward compatibility

/**
 * Map display table names to actual table names
 */
export const TABLE_NAME_MAPPING: Record<AuditTableFilter, string[] | null> = {
  services: ["Service", "ServiceOption"],
  appointments: ["Appointment"],
  workers: ["Worker"],
  customers: ["Customer"],
  salon_settings: ["Salon", "SalonConfiguration"],
  all: null, // null means no filter
};

/**
 * Get human-readable display name for table
 */
export const getTableDisplayName = (tableName: string): string => {
  const mapping: Record<string, string> = {
    Service: "Service",
    ServiceOption: "Service Option",
    Appointment: "Appointment",
    Worker: "Staff Member",
    Customer: "Customer",
    Salon: "Salon Settings",
    SalonConfiguration: "Configuration",
    User: "User",
  };

  return mapping[tableName] || tableName;
};

/**
 * Get human-readable display name for operation
 */
export const getOperationDisplayName = (operation: AuditOperation): string => {
  const mapping: Record<AuditOperation, string> = {
    INSERT: "Created",
    UPDATE: "Updated",
    DELETE: "Deleted",
  };

  return mapping[operation] || operation;
};

/**
 * Format field change for display (legacy)
 */
export const formatFieldChange = (change: any): string => {
  const oldValue = change.formattedOldValue || change.oldValue || "Not set";
  const newValue = change.formattedNewValue || change.newValue || "Not set";

  if (oldValue === newValue) {
    return `${change.displayName}: ${newValue}`;
  }

  return `${change.displayName}: ${oldValue} → ${newValue}`;
};

/**
 * Group audit entries by date
 */
export const groupAuditEntriesByDate = (entries: any[]) => {
  const groups: Record<string, any[]> = {};

  entries.forEach((entry) => {
    const date = new Date(entry.timestamp).toLocaleDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(entry);
  });

  return groups;
};
